import type { ChatMessage } from '../components/ChatWindow';
import type { SuggestedPrompt } from '../components/ChatWindow/SuggestedPrompts';
import React, { createContext, useCallback, useContext, useState } from 'react';
import { getRandomCompanyChatPrompts } from '../components/ChatWindow/SuggestedPrompts';
import { useApi } from '../services/api';
import { UserContext } from '@/shared/contexts/UserContext';

export type ChatSessionKind = 'plan' | 'company' | 'generic';

export interface ChatSessionSummary {
  threadId: string;
  title: string;
  kind: ChatSessionKind;
  /** For plan sessions: accountPlanId. For company: companyId. */
  targetId?: number;
  /** For company sessions: company name used as the URL slug in app-ui. */
  companyName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChatContextType {
  // State
  isOpen: boolean;
  messages: ChatMessage[];
  threadId: string | null;
  isLoading: boolean;
  title: string;
  companyId: number | null;
  suggestedPrompts: SuggestedPrompt[];
  contextInfo: Record<string, number>;
  sessions: ChatSessionSummary[];
  sessionsLoading: boolean;

  // Actions
  openChat: (options?: OpenChatOptions) => void;
  openCompanyChat: (options: OpenCompanyChatOptions) => Promise<void>;
  updateCompanyChatReviews: (reviewIds: number[]) => void;
  closeChat: () => void;
  sendMessage: (message: string) => Promise<void>;
  clearChat: () => void;
  setTitle: (title: string) => void;
  selectSuggestedPrompt: (prompt: SuggestedPrompt) => void;
  loadSessions: () => Promise<void>;
  switchSession: (threadId: string) => Promise<void>;
  startNewSession: () => void;
  deleteSession: (threadId: string) => Promise<void>;
}

export interface OpenChatOptions {
  /** Optional initial message to display (e.g., generated intro message) */
  initialMessage?: string;
  /** Optional system context for the conversation */
  systemContext?: string;
  /** Optional custom title for the chat window */
  title?: string;
}

export interface OpenCompanyChatOptions {
  /** The company ID for scoped conversation */
  companyId: number;
  /** The company name for display */
  companyName: string;
  /** Optional filtered review IDs to include in context */
  reviewIds?: number[];
  /** Whether the user's account has offerings configured */
  hasOfferings?: boolean;
}

const defaultContextValue: ChatContextType = {
  isOpen: false,
  messages: [],
  threadId: null,
  isLoading: false,
  title: 'AI Assistant',
  companyId: null,
  suggestedPrompts: [],
  contextInfo: {},
  sessions: [],
  sessionsLoading: false,
  openChat: () => {},
  openCompanyChat: async () => {},
  updateCompanyChatReviews: () => {},
  closeChat: () => {},
  sendMessage: async () => {},
  clearChat: () => {},
  setTitle: () => {},
  selectSuggestedPrompt: () => {},
  loadSessions: async () => {},
  switchSession: async () => {},
  startNewSession: () => {},
  deleteSession: async () => {},
};

export const ChatContext = createContext<ChatContextType>(defaultContextValue);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [title, setTitle] = useState('AI Assistant');
  const [systemContext, setSystemContext] = useState<string | undefined>(undefined);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [reviewIds, setReviewIds] = useState<number[] | undefined>(undefined);
  const [hasOfferings, setHasOfferings] = useState(false);
  const [suggestedPrompts, setSuggestedPrompts] = useState<SuggestedPrompt[]>([]);
  const [sessions, setSessions] = useState<ChatSessionSummary[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  /**
   * Transform review references in AI responses into clickable markdown links.
   * Handles patterns like "Review #16", "Review 16", "Reviews 2, 9, 19", "(Reviews 2, 9, and 19)", etc.
   */
  const transformReviewReferences = useCallback((content: string, companySlug: string): string => {
    // Helper to create a review link
    const makeLink = (id: string) =>
      `[Review #${id}](/review-reader/${encodeURIComponent(companySlug)}/${id})`;

    // First, handle grouped references like "Reviews 2, 9, 19" or "(Reviews 2, 9, and 19)"
    // This pattern matches "Review(s)" followed by a list of numbers separated by commas/and
    const groupPattern = /\(?\bReviews?\s+([\d,\s]+(?:and\s+\d+)?)\)?/gi;

    let result = content.replace(groupPattern, (match, idList: string) => {
      // Extract all numbers from the list
      const ids = idList.match(/\d+/g);
      if (!ids || ids.length === 0)
        return match;

      // Convert each ID to a link and join with commas
      const links = ids.map(id => makeLink(id));

      // Preserve parentheses if they were in the original
      const hasParens = match.startsWith('(') && match.endsWith(')');
      const formatted = links.length > 1
        ? `${links.slice(0, -1).join(', ')}, and ${links[links.length - 1]}`
        : links[0];

      return hasParens ? `(${formatted})` : formatted;
    });

    // Then handle any remaining single references like "Review #16" or "Review 16"
    // that weren't part of a group (avoid re-processing already linked text)
    const singlePattern = /(?<!\[)\bReview(?:\s(?:ID|#))?[#\s]*(\d+)(?!\]|\))/gi;

    result = result.replace(singlePattern, (_match, reviewId) => {
      return makeLink(reviewId);
    });

    return result;
  }, []);

  const _userContext = useContext(UserContext);
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
  const api = useApi(API_BASE_URL);

  const openChat = useCallback((options?: OpenChatOptions) => {
    // Reset state for new chat
    setMessages([]);
    setThreadId(null);
    setSystemContext(options?.systemContext);
    setTitle(options?.title || 'AI Assistant');
    setCompanyId(null);
    setReviewIds(undefined);
    setHasOfferings(false);

    // If there's an initial message (like a generated intro), display it
    if (options?.initialMessage) {
      setMessages([
        {
          id: `initial-${Date.now()}`,
          content: options.initialMessage,
          role: 'assistant',
          timestamp: new Date(),
        },
      ]);
    }

    setIsOpen(true);
  }, []);

  /**
   * Open a company-scoped chat with automatic context loading and history restoration
   */
  const openCompanyChat = useCallback(async (options: OpenCompanyChatOptions) => {
    const { companyId: cId, companyName: cName, reviewIds: rIds, hasOfferings: hasOfferingsParam } = options;

    // Set company context (this will replace any existing chat)
    setCompanyId(cId);
    setCompanyName(cName);
    setReviewIds(rIds);
    setTitle(`Chat: ${cName}`);
    setMessages([]);
    setThreadId(null);
    setIsOpen(true);
    setIsLoading(true);

    // Fetch actual offerings count if user has an account
    let actualHasOfferings = false;
    if (hasOfferingsParam) {
      try {
        const accountId = _userContext.user?.accountMemberships?.[0]?.accountId;
        if (accountId) {
          const offeringsResponse = await api.get(`/account/${accountId}/offerings`);
          if (!offeringsResponse.error && offeringsResponse.data && offeringsResponse.data.length > 0) {
            actualHasOfferings = true;
          }
        }
      }
      catch (error) {
        console.error('Error fetching offerings count:', error);
        // If we fail to fetch, assume no offerings
      }
    }
    setHasOfferings(actualHasOfferings);

    // Build context-aware opening prompt based on passed-in data
    const hasReviews = rIds && rIds.length > 0;
    let openingPrompt = `I can help you with questions about ${cName}.`;

    const availableData: string[] = [];
    if (hasReviews) {
      availableData.push('deal process notes');
    }
    if (hasOfferings) {
      availableData.push('your offerings');
    }

    if (availableData.length > 0) {
      openingPrompt += ` I have access to ${availableData.join(' and ')} for this company.`;
    }
    else {
      openingPrompt += ' However, there is currently no data available for this company.';
    }

    // Add suggestion if no reviews are available
    if (!hasReviews) {
      openingPrompt += '\n\n⚠️ **No deal process notes are available yet.** Consider creating an [Intelligence Request](/manage-intelligence-requests) to gather insights about this company.';
    }

    openingPrompt += '\n\nWhat would you like to know?';

    try {
      // Load existing chat history for this company
      const historyResponse = await api.get(`/ai/company/${cId}/chat/history`);

      if (!historyResponse.error && historyResponse.messages && historyResponse.messages.length > 0) {
        // Restore previous messages with review links transformed
        const restoredMessages: ChatMessage[] = historyResponse.messages.map((msg: any) => ({
          id: msg.id || `restored-${Date.now()}-${Math.random()}`,
          content: msg.role === 'assistant' ? transformReviewReferences(msg.content, cName) : msg.content,
          role: msg.role as 'user' | 'assistant',
          timestamp: new Date(msg.createdAt),
        }));
        setMessages(restoredMessages);
        setThreadId(historyResponse.threadId);
        // Clear suggested prompts for existing conversations
        setSuggestedPrompts([]);
      }
      else {
        // No history - show welcome message and suggested prompts
        setMessages([
          {
            id: `welcome-${Date.now()}`,
            content: openingPrompt,
            role: 'assistant',
            timestamp: new Date(),
          },
        ]);
        setSuggestedPrompts(getRandomCompanyChatPrompts());
      }
    }
    catch (error) {
      console.error('Error loading company chat history:', error);
      // Show welcome message on error
      setMessages([
        {
          id: `welcome-${Date.now()}`,
          content: openingPrompt,
          role: 'assistant',
          timestamp: new Date(),
        },
      ]);
      setSuggestedPrompts(getRandomCompanyChatPrompts());
    }
    finally {
      setIsLoading(false);
    }
  }, [api, transformReviewReferences]);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  /**
   * Update review IDs for an already-open company chat
   * Useful when reviews load after the chat has been opened
   */
  const updateCompanyChatReviews = useCallback((reviewIds: number[]) => {
    setReviewIds(reviewIds);
  }, []);

  const clearChat = useCallback(() => {
    setMessages([]);
    setThreadId(null);
    setSystemContext(undefined);
    setTitle('AI Assistant');
    setCompanyId(null);
    setCompanyName(null);
    setReviewIds(undefined);
    setHasOfferings(false);
  }, []);

  /**
   * Fetch the list of this user's generic chat sessions from the API.
   */
  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const res = await api.get('/ai/chat/sessions');
      if (!res?.error && Array.isArray(res?.sessions)) {
        setSessions(res.sessions);
      }
    }
    catch (error) {
      console.error('Error loading chat sessions:', error);
    }
    finally {
      setSessionsLoading(false);
    }
  }, [api]);

  const sendMessage = useCallback(async (message: string) => {
    if (!message.trim() || isLoading)
      return;

    // Clear suggested prompts once user starts chatting
    setSuggestedPrompts([]);

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      content: message,
      role: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      let response;

      // Use company-scoped endpoint if companyId is set
      if (companyId) {
        response = await api.post(`/ai/company/${companyId}/chat`, {
          message,
          reviewIds,
        });
      }
      else {
        // Fall back to generic chat endpoint
        response = await api.post('/ai/chat', {
          threadId,
          message,
          systemContext,
        });
      }

      if (response.error) {
        const errorMessage: ChatMessage = {
          id: `error-${Date.now()}`,
          content: `Sorry, I couldn't process your message. ${response.message || 'Please try again.'}`,
          role: 'assistant',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, errorMessage]);
      }
      else {
        // Save threadId for continuation
        const isNewGenericThread = !companyId && !threadId && !!response.threadId;
        if (response.threadId) {
          setThreadId(response.threadId);
        }

        // Transform review references to clickable links if in company context
        const responseContent = companyName
          ? transformReviewReferences(response.response, companyName)
          : response.response;

        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          content: responseContent,
          role: 'assistant',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, assistantMessage]);

        // A new generic thread was just created + auto-titled server-side;
        // refresh the sidebar list so it appears.
        if (isNewGenericThread) {
          loadSessions();
        }
      }
    }
    catch (error) {
      console.error('Chat error:', error);
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        content: 'Sorry, an error occurred. Please try again.',
        role: 'assistant',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    }
    finally {
      setIsLoading(false);
    }
  }, [api, threadId, systemContext, isLoading, companyId, companyName, reviewIds, transformReviewReferences, loadSessions]);

  /**
   * Handle selecting a suggested prompt - sends it as a user message
   */
  const selectSuggestedPrompt = useCallback((prompt: SuggestedPrompt) => {
    sendMessage(prompt.text);
  }, [sendMessage]);

  /**
   * Switch to a specific existing session — loads its messages and binds
   * future sends to that thread.
   */
  const switchSession = useCallback(async (targetThreadId: string) => {
    setIsLoading(true);
    setMessages([]);
    setCompanyId(null);
    setCompanyName(null);
    setReviewIds(undefined);
    setHasOfferings(false);
    setSystemContext(undefined);
    setSuggestedPrompts([]);
    setIsOpen(true);

    try {
      const res = await api.get(`/ai/chat/sessions/${encodeURIComponent(targetThreadId)}`);
      if (!res?.error && Array.isArray(res?.messages)) {
        const restored: ChatMessage[] = res.messages.map((msg: any) => ({
          id: msg.id || `restored-${Date.now()}-${Math.random()}`,
          content: msg.content,
          role: msg.role === 'assistant' ? 'assistant' : 'user',
          timestamp: msg.createdAt ? new Date(msg.createdAt) : new Date(),
        }));
        setMessages(restored);
        setThreadId(res.threadId || targetThreadId);
        const matched = sessions.find(s => s.threadId === targetThreadId);
        setTitle(matched?.title || 'AI Assistant');
      }
    }
    catch (error) {
      console.error('Error switching chat session:', error);
    }
    finally {
      setIsLoading(false);
    }
  }, [api, sessions]);

  /**
   * Start a fresh conversation — clears state; a new threadId will be
   * minted server-side on the first message.
   */
  const startNewSession = useCallback(() => {
    setMessages([]);
    setThreadId(null);
    setSystemContext(undefined);
    setCompanyId(null);
    setCompanyName(null);
    setReviewIds(undefined);
    setHasOfferings(false);
    setSuggestedPrompts([]);
    setTitle('AI Assistant');
    setIsOpen(true);
  }, []);

  /**
   * Delete a session. If it's the active one, also reset the UI to a
   * blank state.
   */
  const deleteSession = useCallback(async (targetThreadId: string) => {
    try {
      const res = await api.delete(`/ai/chat/sessions/${encodeURIComponent(targetThreadId)}`);
      if (!res?.error) {
        setSessions(prev => prev.filter(s => s.threadId !== targetThreadId));
        if (threadId === targetThreadId) {
          setMessages([]);
          setThreadId(null);
          setTitle('AI Assistant');
        }
      }
    }
    catch (error) {
      console.error('Error deleting chat session:', error);
    }
  }, [api, threadId]);

  const value: ChatContextType = {
    isOpen,
    messages,
    threadId,
    isLoading,
    title,
    companyId,
    suggestedPrompts,
    contextInfo: {
      ...(reviewIds && reviewIds.length > 0 ? { 'Deal Reviews': reviewIds.length } : {}),
      ...(hasOfferings ? { 'Your Offerings': 1 } : {}),
    },
    sessions,
    sessionsLoading,
    openChat,
    openCompanyChat,
    updateCompanyChatReviews,
    closeChat,
    sendMessage,
    clearChat,
    setTitle,
    selectSuggestedPrompt,
    loadSessions,
    switchSession,
    startNewSession,
    deleteSession,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

// Convenience hook
export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}
